import { approvalRatePerIndustryCode } from "./approvalRatePerIndustryCode"
import { cvrIndustryCodeToFcpRiskScore } from "./fcpRiskScore"

export interface CVRInfo {
    cvr: number
    name: string
    email?: string
    phone?: string
    industryCode: string
    industryName: string
    industryCategory: string
    companyType: string
    companyTypeShort?: string
    protected: boolean
    marketingConsent: boolean
    city: string
    postalCode: number
    foundedDateTimestamp: number
    companyTypeLastChangeAt?: number
    companyIndustryLastChangeAt?: number
    owners?: string[]
    website?: string
    riskScore?: number
    approvalRate?: number
    purpose?: string
}
export async function getCVRInfo(cvr: number, retries = 1): Promise<Partial<CVRInfo>> {
    if (!cvr || cvr?.toString()?.length !== 8) {
        console.log("Invalid CVR number", { cvr })
        return {}
    }
    try {
        const url = "http://distribution.virk.dk/cvr-permanent/virksomhed/_search"
        const query = {
            query: {
                bool: {
                    must: [
                        {
                            term: {
                                "Vrvirksomhed.cvrNummer": cvr,
                            },
                        },
                    ],
                },
            },
        }
        const res = await fetch(url, {
            method: "POST",
            signal: AbortSignal.timeout(4000),
            body: JSON.stringify(query),
            headers: {
                "Authorization": "Basic " + Buffer.from(process.env.VIRK_API_USERNAME_AND_PASSWORD).toString("base64"),
                "Content-Type": "application/json",
            },
        })

        const json = await res.json()
        const hits = json?.hits
        if (!hits?.hits.length) return {}
        const cvrRes: VirkCVRResult = hits.hits[0]._source.Vrvirksomhed
        // if (1 === 1) return cvrRes

        // ..(cvrRes?.industrycode ? {
        //     "CVR industry code": cvrRes?.industrycode,
        //     "CVR industry name": cvrRes?.industrydesc,
        //     "CVR company name": cvrRes?.name,
        //     "CVR company type": cvrRes?.companydesc,
        //     "CVR company founded at": (new Date(cvrRes?.startdate)).getTime() / 1000,
        //     "CVR marketing consent": cvrRes?.protected == false,
        //     "FCP risk score": fcpRiskScore[cvrRes?.industrycode],
        //     "CVR industry approval rate": approvalRatePerIndustryCode[cvrRes?.industrycode], // 0-100

        const cvrInfo = cvrResultToCvrInfo(cvrRes)

        console.log("Successfully fetched CVR from virk", {
            cvr,
            name: cvrInfo.name,
        })
        return cvrInfo
    } catch (error) {
        if (retries > 0) {
            return getCVRInfo(cvr, retries - 1)
        } else {
            console.error("All retry attempts failed for datacvr.virk.dk", {
                cvr,
                error: error.toString(),
            })
            return await getCvrInfoFromCvrApiDk(cvr)
        }
    }
}

function cvrResultToCvrInfo(cvrResult: VirkCVRResult) {
    const [year, month, day] = cvrResult.virksomhedMetadata.stiftelsesDato
        ?.split("-")
        ?.filter(Boolean)
        ?.map(Number) ?? [NaN, NaN, NaN]
    const foundedDate = new Date(Date.UTC(year, month - 1, day, 2))

    const owners = cvrResult.deltagerRelation
        .filter((relation) => relation?.deltager?.enhedstype === "PERSON")
        .map((relation) => relation.deltager.navne[0].navn)

    const email =
        cvrResult.virksomhedMetadata.nyesteKontaktoplysninger?.find((kontakt) => kontakt.includes("@")) ||
        cvrResult.elektroniskPost?.[0]?.kontaktoplysning
    const phone =
        cvrResult.virksomhedMetadata.nyesteKontaktoplysninger?.find(
            (kontakt) => !kontakt.includes("@") && !kontakt.includes("."),
        ) || cvrResult.telefonNummer?.[0]?.kontaktoplysning
    const website =
        cvrResult.virksomhedMetadata.nyesteKontaktoplysninger?.find(
            (kontakt) => !kontakt.includes("@") && kontakt.includes("."),
        ) || cvrResult.hjemmeside?.[0]?.kontaktoplysning
    const name = cvrResult.virksomhedMetadata?.nyesteNavn?.navn || cvrResult.navne?.[0]?.navn
    const industryCode =
        cvrResult.virksomhedMetadata?.nyesteHovedbranche?.branchekode || cvrResult.hovedbranche?.[0].branchekode
    const purpose = cvrResult?.attributter?.find((attr) => attr.type === "FORMÅL")?.vaerdier?.[0]?.vaerdi
    const cvrInfo = {
        cvr: cvrResult.cvrNummer,
        name,
        email,
        phone,
        industryCode,
        industryName:
            cvrResult.virksomhedMetadata?.nyesteHovedbranche?.branchetekst || cvrResult.hovedbranche?.[0].branchetekst,
        industryCategory: industryCodeToTopLevelBranch(industryCode),
        companyType:
            cvrResult.virksomhedMetadata?.nyesteVirksomhedsform?.langBeskrivelse ||
            cvrResult.virksomhedsform?.[0].langBeskrivelse,
        companyTypeLastChangeAt: cvrResult.virksomhedMetadata?.nyesteVirksomhedsform?.sidstOpdateret
            ? new Date(cvrResult.virksomhedMetadata?.nyesteVirksomhedsform?.sidstOpdateret).getTime() / 1000
            : null,
        companyIndustryLastChangeAt: cvrResult.virksomhedMetadata?.nyesteHovedbranche?.sidstOpdateret
            ? new Date(cvrResult.virksomhedMetadata?.nyesteHovedbranche?.sidstOpdateret).getTime() / 1000
            : null,
        // companyTypeShort: cvrResult.virksomhedMetadata?.nyesteVirksomhedsform?.kortBeskrivelse || cvrResult.virksomhedsform?.[0].kortBeskrivelse,
        protected: cvrResult.reklamebeskyttet,
        marketingConsent: !cvrResult.reklamebeskyttet,
        city:
            cvrResult.virksomhedMetadata?.nyesteBeliggenhedsadresse?.postdistrikt ||
            cvrResult.beliggenhedsadresse?.[0]?.postdistrikt,
        postalCode:
            cvrResult.virksomhedMetadata?.nyesteBeliggenhedsadresse?.postnummer ||
            cvrResult.beliggenhedsadresse?.[0]?.postnummer,
        foundedDateTimestamp: foundedDate.getTime(),
        owners,
        website,
        riskScore: cvrIndustryCodeToFcpRiskScore[industryCode], // 1-5
        approvalRate: approvalRatePerIndustryCode[industryCode], // 1-100
        purpose,
        numberOfEmployees: cvrResult.virksomhedMetadata?.nyesteErstMaanedsbeskaeftigelse?.antalAnsatte ?? undefined,
    }

    return cvrInfo
}

async function getCvrInfoFromCvrApiDk(cvr: number): Promise<CVRInfo> {
    const res = await fetch(`https://cvrapi.dk/api?search=${cvr}&country=dk`, {
        headers: {
            "user-agent": "Lunar.app (anpo@lunar.app)",
        },
    })
    const data: CvrApiDkRoot = await res.json()
    if (data?.name) {
        const [day, month, year] = data.startdate.split(/\/| - /)
        const foundedDate = new Date(Number(year), Number(month) - 1, Number(day))
        return {
            cvr: data.vat,
            name: data.name,
            email: data.email,
            phone: data.phone,
            industryCode: data.industrycode.toString(),
            industryName: data.industrydesc,
            industryCategory: industryCodeToTopLevelBranch(data.industrycode.toString()),
            companyType: data.companydesc,
            protected: data.protected,
            marketingConsent: !data.protected,
            city: data.city,
            postalCode: Number(data.zipcode),
            foundedDateTimestamp: foundedDate.getTime(),
            owners: data.owners?.map((owner) => owner.name),
            website: data.website,
            riskScore: cvrIndustryCodeToFcpRiskScore[data.industrycode.toString()],
            approvalRate: approvalRatePerIndustryCode[data.industrycode.toString()],
        }
    }
}

export function industryCodeToTopLevelBranch(industryCode: string): string {
    const topLevelBranchesCodeMap = {
        "A: Landbrug, jagt, skovbrug og fiskeri": [10000, 40000],
        "B: Råstofindvinding": [50000, 100000],
        "C: Fremstillingsvirksomhed": [100000, 340000],
        "D: El-, gas- og fjernvarmeforsyning": [350000, 360000],
        "E: Vandforsyning; kloakvæsen, affaldshåndtering og rensning af jord og grundvand": [360000, 400000],
        "F: Bygge- og anlægsvirksomhed": [410000, 440000],
        "G: Engroshandel og detailhandel; reparation af motorkøretøjer og motorcykler": [450000, 480000],
        "H: Transport og godshåndtering": [490000, 540000],
        "I: Overnatningsfaciliteter og restaurationsvirksomhed": [550000, 570000],
        "J: Information og kommunikation": [580000, 640000],
        "K: Pengeinstitut- og finansvirksomhed, forsikring": [640000, 670000],
        "L: Fast ejendom": [680000, 690000],
        "M: Liberale, videnskabelige og tekniske tjenesteydelser": [690000, 760000],
        "N: Administrative tjenesteydelser og hjælpetjenester": [770000, 830000],
        "O: Offentlig forvaltning og forsvar; socialsikring": [840000, 890000],
        "P: Undervisning": [850000, 860000],
        "Q: Sundhedsvæsen og sociale foranstaltninger": [860000, 890000],
        "R: Kultur, forlystelser og sport": [900000, 940000],
        "S: Andre serviceydelser": [940000, 970000],
        "T: Private husholdninger med ansat medhjælp; husholdningers produktion af varer og tjenesteydelser til eget brug, i.a.n.":
            [970000, 990000],
        "U: Eksterritoriale organisationer og organer": [990000, 990000],
    }

    let result = ""
    Object.entries(topLevelBranchesCodeMap).forEach(([branch, [from, to]]) => {
        if (Number(industryCode) >= from && Number(industryCode) < to) {
            result = branch
        }
    })
    return result
}

export async function searchCVRByName(q: string, retries = 1): Promise<CVRInfo[]> {
    if (!q || q?.toString()?.length < 3) {
        console.log("Invalid query", { q })
        return []
    }
    try {
        const escaped = q.replace(/(["\\])/g, "\\$1").replace(/\//g, "\\/")
        const safeQuery = `"${escaped}"`
        const url = "http://distribution.virk.dk/cvr-permanent/virksomhed/_search"
        const res = await fetch(url + "?q=" + encodeURIComponent(safeQuery), {
            method: "GET",
            headers: {
                Authorization: "Basic " + Buffer.from(process.env.VIRK_API_USERNAME_AND_PASSWORD).toString("base64"),
                "Content-Type": "application/json",
            },
        })

        const json = await res.json()
        const hits: { _score: number; _source: { Vrvirksomhed: VirkCVRResult } }[] = json?.hits?.hits
        if (!hits?.length) return []
        return hits.map((hit) => cvrResultToCvrInfo(hit._source.Vrvirksomhed))
    } catch (error) {
        if (retries > 0) {
            return searchCVRByName(q, retries - 1)
        }
        console.error("All retry attempts failed for datacvr.virk.dk", {
            q,
            error: error.toString(),
        })
        return []
    }
}

export interface VirkCVRResult {
    cvrNummer: number
    regNummer: any[]
    brancheAnsvarskode: any
    reklamebeskyttet: boolean
    navne: Navne[]
    binavne: any[]
    postadresse: any[]
    beliggenhedsadresse: Beliggenhedsadresse[]
    telefonNummer: any[]
    telefaxNummer: any[]
    sekundaertTelefonNummer: any[]
    sekundaertTelefaxNummer: any[]
    elektroniskPost: any[]
    hjemmeside: any[]
    obligatoriskEmail: any[]
    livsforloeb: Livsforloeb[]
    hovedbranche: Hovedbranche[]
    bibranche1: any[]
    bibranche2: any[]
    bibranche3: any[]
    status: any[]
    virksomhedsstatus: Status[]
    virksomhedsform: Virksomhedsform[]
    aarsbeskaeftigelse: any[]
    kvartalsbeskaeftigelse: any[]
    maanedsbeskaeftigelse: any[]
    erstMaanedsbeskaeftigelse: any[]
    attributter: Attributter[]
    penheder: Penheder[]
    deltagerRelation: DeltagerRelation[]
    fusioner: any[]
    spaltninger: any[]
    virksomhedMetadata: VirksomhedMetadata
    samtId: number
    fejlRegistreret: boolean
    dataAdgang: number
    enhedsNummer: number
    enhedstype: string
    sidstIndlaest: string
    sidstOpdateret: string
    fejlVedIndlaesning: boolean
    naermesteFremtidigeDato: any
    fejlBeskrivelse: any
    virkningsAktoer: string
}

export interface Navne {
    navn: string
    periode: Periode
    sidstOpdateret: string
}

export interface Periode {
    gyldigFra: string
    gyldigTil: any
}

export interface Beliggenhedsadresse {
    landekode: string
    fritekst: any
    vejkode: number
    kommune: Kommune
    husnummerFra: number
    adresseId: string
    sidstValideret: string
    husnummerTil: any
    bogstavFra: any
    bogstavTil: any
    etage: any
    sidedoer: any
    conavn: any
    postboks: any
    vejnavn: string
    bynavn: any
    postnummer: number
    postdistrikt: string
    periode: Periode3
    sidstOpdateret: string
}

export interface Kommune {
    kommuneKode: number
    kommuneNavn: string
    periode: Periode2
    sidstOpdateret: string
}

export interface Periode2 {
    gyldigFra: string
    gyldigTil: any
}

export interface Periode3 {
    gyldigFra: string
    gyldigTil: any
}

export interface Livsforloeb {
    periode: Periode4
    sidstOpdateret: string
}

export interface Periode4 {
    gyldigFra: string
    gyldigTil: any
}

export interface Hovedbranche {
    branchekode: string
    branchetekst: string
    periode: Periode5
    sidstOpdateret: string
}

export interface Periode5 {
    gyldigFra: string
    gyldigTil: any
}

export interface Status {
    status: string
    periode: Periode6
    sidstOpdateret: string
}

export interface Periode6 {
    gyldigFra: string
    gyldigTil: any
}

export interface Virksomhedsform {
    virksomhedsformkode: number
    kortBeskrivelse: string
    langBeskrivelse: string
    ansvarligDataleverandoer: string
    periode: Periode7
    sidstOpdateret: string
}

export interface Periode7 {
    gyldigFra: string
    gyldigTil: any
}

export interface Attributter {
    sekvensnr: number
    type: string
    vaerditype: string
    vaerdier: Vaerdier[]
}

export interface Vaerdier {
    vaerdi: string
    periode: Periode8
    sidstOpdateret: string
}

export interface Periode8 {
    gyldigFra: string
    gyldigTil: any
}

export interface Penheder {
    pNummer: number
    periode: Periode9
    sidstOpdateret: string
}

export interface Periode9 {
    gyldigFra: string
    gyldigTil: any
}

export interface DeltagerRelation {
    deltager: Deltager
    kontorsteder: any[]
    organisationer: Organisationer[]
}

export interface Deltager {
    enhedsNummer: number
    enhedstype: string
    forretningsnoegle?: number
    organisationstype: any
    sidstIndlaest: string
    sidstOpdateret: string
    navne: Navne2[]
    adresseHemmelig?: boolean
    adresseHemmeligUndtagelse?: boolean
    adresseOpdateringOphoert?: boolean
    beliggenhedsadresse: Beliggenhedsadresse2[]
    postadresse: any[]
}

export interface Navne2 {
    navn: string
    periode: Periode10
    sidstOpdateret?: string
}

export interface Periode10 {
    gyldigFra?: string
    gyldigTil: any
}

export interface Beliggenhedsadresse2 {
    landekode: string
    fritekst: any
    vejkode: number
    kommune: Kommune2
    husnummerFra: number
    adresseId?: string
    sidstValideret?: string
    husnummerTil: any
    bogstavFra: any
    bogstavTil: any
    etage: any
    sidedoer: any
    conavn: any
    postboks: any
    vejnavn: string
    bynavn: any
    postnummer: number
    postdistrikt: string
    periode: Periode12
    sidstOpdateret: string
}

export interface Kommune2 {
    kommuneKode: number
    kommuneNavn: string
    periode: Periode11
    sidstOpdateret: string
}

export interface Periode11 {
    gyldigFra: string
    gyldigTil: any
}

export interface Periode12 {
    gyldigFra: string
    gyldigTil: any
}

export interface Organisationer {
    enhedsNummerOrganisation: number
    hovedtype: string
    organisationsNavn: OrganisationsNavn[]
    attributter: Attributter2[]
    medlemsData: MedlemsDaum[]
}

export interface OrganisationsNavn {
    navn: string
    periode: Periode13
    sidstOpdateret: string
}

export interface Periode13 {
    gyldigFra: string
    gyldigTil: any
}

export interface Attributter2 {
    sekvensnr: number
    type: string
    vaerditype: string
    vaerdier: Vaerdier2[]
}

export interface Vaerdier2 {
    vaerdi: string
    periode: Periode14
    sidstOpdateret: string
}

export interface Periode14 {
    gyldigFra: string
    gyldigTil: any
}

export interface MedlemsDaum {
    attributter: Attributter3[]
}

export interface Attributter3 {
    sekvensnr: number
    type: string
    vaerditype: string
    vaerdier: Vaerdier3[]
}

export interface Vaerdier3 {
    vaerdi: string
    periode: Periode15
    sidstOpdateret: string
}

export interface Periode15 {
    gyldigFra: string
    gyldigTil: any
}

export interface VirksomhedMetadata {
    nyesteNavn: NyesteNavn
    nyesteBinavne: any[]
    nyesteVirksomhedsform: NyesteVirksomhedsform
    nyesteBeliggenhedsadresse: NyesteBeliggenhedsadresse
    nyesteHovedbranche: NyesteHovedbranche
    nyesteBibranche1: any
    nyesteBibranche2: any
    nyesteBibranche3: any
    nyesteStatus: any
    nyesteKontaktoplysninger: any[]
    antalPenheder: number
    nyesteAarsbeskaeftigelse: any
    nyesteKvartalsbeskaeftigelse: any
    nyesteMaanedsbeskaeftigelse: any
    nyesteErstMaanedsbeskaeftigelse?: NyesteErstMaanedsbeskaeftigelse
    sammensatStatus: string
    stiftelsesDato: string
    virkningsDato: string
}

export interface NyesteNavn {
    navn: string
    periode: Periode16
    sidstOpdateret: string
}

export interface Periode16 {
    gyldigFra: string
    gyldigTil: any
}

export interface NyesteVirksomhedsform {
    virksomhedsformkode: number
    kortBeskrivelse: string
    langBeskrivelse: string
    ansvarligDataleverandoer: string
    periode: Periode17
    sidstOpdateret: string
}

export interface Periode17 {
    gyldigFra: string
    gyldigTil: any
}

export interface NyesteBeliggenhedsadresse {
    landekode: string
    fritekst: any
    vejkode: number
    kommune: Kommune3
    husnummerFra: number
    adresseId: string
    sidstValideret: string
    husnummerTil: any
    bogstavFra: any
    bogstavTil: any
    etage: any
    sidedoer: any
    conavn: any
    postboks: any
    vejnavn: string
    bynavn: any
    postnummer: number
    postdistrikt: string
    periode: Periode19
    sidstOpdateret: string
}

export interface Kommune3 {
    kommuneKode: number
    kommuneNavn: string
    periode: Periode18
    sidstOpdateret: string
}

export interface Periode18 {
    gyldigFra: string
    gyldigTil: any
}

export interface Periode19 {
    gyldigFra: string
    gyldigTil: any
}

export interface NyesteHovedbranche {
    branchekode: string
    branchetekst: string
    periode: Periode20
    sidstOpdateret: string
}
export interface NyesteErstMaanedsbeskaeftigelse {
    antalAnsatte: number
}

export interface Periode20 {
    gyldigFra: string
    gyldigTil: any
}

export interface CvrApiDkRoot {
    vat: number
    name: string
    address: string
    zipcode: string
    city: string
    cityname: string
    protected: boolean
    phone: string
    email: string
    fax: any
    startdate: string
    enddate: any
    employees: number
    addressco: string
    industrycode: number
    industrydesc: string
    companycode: number
    companydesc: string
    creditstartdate: any
    creditbankrupt: boolean
    creditstatus: any
    owners: CvrApiDkOwner[]
    productionunits: CvrApiDkProductionunit[]
    t: number
    version: number
    website?: string
}

export interface CvrApiDkOwner {
    name: string
}

export interface CvrApiDkProductionunit {
    pno: number
    main: boolean
    name: string
    address: string
    zipcode: string
    city: string
    cityname?: string
    protected: boolean
    phone: string
    email: string
    fax?: string
    startdate: string
    enddate?: string
    employees: number
    addressco?: string
    industrycode: number
    industrydesc: string
}
